/**
 * Copyright (c) Microsoft. All rights reserved.
 */

import flatten from 'lodash.flatten';
import orderBy from 'lodash.orderby';
import { observer } from 'mobx-react-lite';
import * as React from 'react';
import { useHistory } from 'react-router';
import { EditableTitle } from 'src/core/components/EditableTitle';
import { LimitedAccessToFeaturesMessageBar } from 'src/core/components/LimitedAccessToFeaturesMessageBar';
import { Page } from 'src/core/components/Page';
import { RenameModal, RenameModalDto } from 'src/core/components/RenameModal';
import { TablePagination } from 'src/core/components/TablePagination';
import { SortKeyType } from 'src/core/components/TableProps';
import { useAnnounced } from 'src/core/hooks/useAnnounced';
import { useGlobalState } from 'src/core/hooks/useGlobalState';
import { useLocalization } from 'src/core/hooks/useLocalization';
import { usePagination } from 'src/core/hooks/usePagination';
import { useProgressTracker } from 'src/core/hooks/useProgressTracker';
import { useSelection } from 'src/core/hooks/useSelection';
import { useUpdateState } from 'src/core/hooks/useUpdateState';
import { getEventLogger } from 'src/core/services/telemetry/logEventBuilder';
import { getStylistV2 } from 'src/core/themes/luisStylist';
import { mapUserRoleToUserActions } from 'src/core/utils/azureUtils';
import { LuisConstants } from 'src/core/utils/LuisConstants';
import { routeTable } from 'src/core/utils/routeTable';
import { substringSearchStrategy } from 'src/core/utils/searchStrategies/substringSearchStrategy';
import { createKeyedStringNormalizer } from 'src/core/utils/strings';
import { DomainModelsModal } from 'src/luis/appShell/buildPane/domainsPage/DomainModelsModal';
import { entitiesPageMessages } from 'src/luis/appShell/buildPane/entitiesPage/entitiesPageMessages';
import { useCurrentVersionedAppId } from 'src/luis/modules/apps/hooks/useCurrentVersionedAppId';
import { PrebuiltEntitiesModal } from 'src/luis/modules/entities/components/prebuiltEntitiesModal/PrebuiltEntitiesModal';
import {
    deleteEntity as deleteEntityHelper,
    renameEntity as renameEntityHelper
} from 'src/luis/modules/entities/utils/dispatchEntityHelper';
import { getModelsUsedByFeature } from 'src/luis/modules/features/utils/conversationFeatureUtils';
import { useModels } from 'src/luis/modules/models/useModels';
import { entityDenormalizer } from 'src/luis/projects/entities/utils/entitiesDenormalizer';
import { ModelType } from 'src/luis/types/ModelType';
import { EntityStore } from 'src/luis/types/stores/entities/EntityStore';
import { CreateEntityModal } from 'src/luis/verticals/createEntityModal/CreateEntityModal';
import { DeleteEntityModal } from 'src/luis/verticals/deleteEntityModal/DeleteEntityModal';
import { EntityTableCommandBar } from 'src/luis/verticals/entityTable/components/EntityTableCommandBar';
import { EntityTable } from 'src/luis/verticals/entityTable/EntityTable';

const defaultModalStates = {
    isCreateNewEntityHidden: true,
    isRenameEntityModalHidden: true,
    isDeleteEntityModalHidden: true,
    isPrebuiltEntityDomainsModalHidden: true,
    isPrebuiltEntityModalHidden: true
};

const { getClassName } = getStylistV2('NewEntitiesPage');
const titleClassName = getClassName('title', { marginBottom: '48px' });

/**
 * Represents a page container component for entities.
 */
export const EntitiesPage = observer(() => {
    const history = useHistory();
    const { localize } = useLocalization();
    const { dispatcher, rootStore } = useGlobalState();
    const { appId, versionId } = useCurrentVersionedAppId();
    const { announcedComponent, announceAction } = useAnnounced();
    const eventLoggerRef = React.useRef(getEventLogger('EntitiesPage'));
    const { intentStores, entityStores, phraseListStores, isLoading } = useModels();

    const { selection, selectedItems } = useSelection<EntityStore>();
    const [sortKey, setSortKey] = React.useState<SortKeyType<EntityStore>>({ key: 'name', order: 'asc' });

    const [query, setQuery] = React.useState('');
    const [modalsState, setModalsState] = useUpdateState(defaultModalStates);

    const { userRole, displayedUserRole } = rootStore.serviceKey;
    const { canWrite, canDeleteData } = mapUserRoleToUserActions(userRole);
    const filteredEntities = orderBy(
        substringSearchStrategy(entityStores, query, 'name'),
        [createKeyedStringNormalizer(sortKey.key)],
        [sortKey.order]
    );
    const { currentPage, setCurrentPage, pageItems, pageCount } = usePagination(
        filteredEntities,
        LuisConstants.ResourceLimits.DefaultPageSize
    );

    const isRenameEntityDisabled = selectedItems[0]?.typeId === ModelType.Prebuilt;

    const modelsUsedBySelectedEntities = React.useMemo(
        () => flatten(selectedItems.map(item => getModelsUsedByFeature(item, [...entityStores, ...intentStores]))),
        [entityStores, intentStores, selectedItems]
    );

    const onCompleteTracker = (announceActionMsg: string) => {
        setModalsState(defaultModalStates);
        announceAction(announceActionMsg);
        selection.setAllSelected(false);
    };

    const deleteEntityTracker = useProgressTracker({
        onComplete: () => onCompleteTracker(localize(entitiesPageMessages.deletionOperationCompleted))
    });

    const renameEntityTracker = useProgressTracker({
        onComplete: () => onCompleteTracker(localize(entitiesPageMessages.renamingOperationCompleted))
    });

    /**
     * Callback function to handle the delete entity action.
     */
    const deleteEntity = () => {
        deleteEntityHelper(
            selectedItems,
            dispatcher,
            deleteEntityTracker,
            {
                running: { title: localize(entitiesPageMessages.deleteEntityToastStart) },
                completed: { title: localize(entitiesPageMessages.deleteEntityToastSuccess) },
                failed: { title: localize(entitiesPageMessages.deleteEntityToastFailed) }
            },
            {
                running: { title: localize(entitiesPageMessages.deleteEntitiesToastStart) },
                completed: { title: localize(entitiesPageMessages.deleteEntitiesToastSuccess) },
                failed: { title: localize(entitiesPageMessages.deleteEntitiesToastFailed) }
            }
        );
        setModalsState({ isDeleteEntityModalHidden: true });
    };

    /**
     * Callback function to handle the rename entity action.
     *
     * @param modalData The data to rename the entity with.
     */
    const renameEntity = (modalData: RenameModalDto): void => {
        const { name } = modalData;
        const entityStore = selectedItems[0];

        const denormalizedEntity = entityDenormalizer(entityStores, intentStores, phraseListStores)(entityStore);

        renameEntityHelper(denormalizedEntity, name, dispatcher, renameEntityTracker, {
            running: { title: localize(entitiesPageMessages.updateEntityToastStart) },
            completed: { title: localize(entitiesPageMessages.updateEntityToastSuccess) },
            failed: { title: localize(entitiesPageMessages.updateEntityToastFailed) }
        });
    };

    /**
     * Closes entity creation modal and logEvent for
     * the create entity submit.
     */
    const onCreateEntitySubmit = (entityStore: EntityStore) => {
        setModalsState({ isCreateNewEntityHidden: true });
        eventLoggerRef.current.end('CreateEntityDialog');

        if (entityStore?.id) {
            history.push(routeTable.luis.entityPage(appId, versionId, entityStore.id));
        }
    };

    /**
     * Closes entity creation modal and logEvent for the modal dismissal.
     */
    const onCreateEntityDismiss = () => {
        setModalsState({ isCreateNewEntityHidden: true });
        eventLoggerRef.current.log('CreateEntityDialogDismissed');
    };

    /**
     * Opens entity creation modal and logEvent for the modal opening.
     */
    const onOpenCreateEntityModal = () => {
        setModalsState({ isCreateNewEntityHidden: false });
        eventLoggerRef.current.start('CreateEntityDialog');
    };

    const onQueryChange = (searchQuery: string) => {
        setQuery(searchQuery);
        eventLoggerRef.current.log('EntitySearchTriggered');
    };

    return (
        <Page pageName="EntitiesPage" size="container-fluid" title={localize(entitiesPageMessages.pageTitle)}>
            <EditableTitle
                className={titleClassName}
                descriptionAlignment="center"
                title={localize(entitiesPageMessages.pageTitle)}
                description={localize(entitiesPageMessages.pageTitleDescription)}
            />
            {!canWrite && <LimitedAccessToFeaturesMessageBar displayedUserRole={displayedUserRole} />}
            <EntityTableCommandBar
                query={query}
                onQueryChange={onQueryChange}
                selectedItems={selectedItems}
                onCreateEntity={onOpenCreateEntityModal}
                isRenameEntityDisabled={isRenameEntityDisabled}
                onAddPrebuiltDomainEntity={() => setModalsState({ isPrebuiltEntityDomainsModalHidden: false })}
                onAddPrebuiltEntity={() => setModalsState({ isPrebuiltEntityModalHidden: false })}
                onDeleteEntities={() => setModalsState({ isDeleteEntityModalHidden: false })}
                onRenameEntity={() => setModalsState({ isRenameEntityModalHidden: false })}
                userRole={userRole}
                displayedUserRole={displayedUserRole}
            />

            <CreateEntityModal
                hidden={modalsState.isCreateNewEntityHidden}
                entityName=""
                allowedModelTypes={[ModelType.Simple, ModelType.List, ModelType.Regex, ModelType.PatternAny]}
                onSubmit={onCreateEntitySubmit}
                onDismiss={onCreateEntityDismiss}
            />

            <DomainModelsModal
                modelsType="entities"
                hidden={modalsState.isPrebuiltEntityDomainsModalHidden}
                onDismiss={() => setModalsState({ isPrebuiltEntityDomainsModalHidden: true })}
                onSubmit={() => setModalsState({ isPrebuiltEntityDomainsModalHidden: true })}
            />

            <PrebuiltEntitiesModal
                hidden={modalsState.isPrebuiltEntityModalHidden}
                onDismiss={() => setModalsState({ isPrebuiltEntityModalHidden: true })}
                onSubmit={() => setModalsState({ isPrebuiltEntityModalHidden: true })}
            />

            <RenameModal
                onSubmit={renameEntity}
                disabled={renameEntityTracker.isRunning}
                hidden={modalsState.isRenameEntityModalHidden}
                name={selectedItems[0] ? selectedItems[0].name : null}
                onDismiss={() => setModalsState({ isRenameEntityModalHidden: true })}
                dialogContentProps={{ title: localize(entitiesPageMessages.renameModalTitle) }}
            />

            <DeleteEntityModal
                entityNames={selectedItems.map(selectedItem => selectedItem.name)}
                usedByEntityNames={modelsUsedBySelectedEntities.filter(u => u.typeId !== ModelType.Intent).map(u => u.name)}
                usedByIntentNames={modelsUsedBySelectedEntities.filter(u => u.typeId === ModelType.Intent).map(u => u.name)}
                disabled={deleteEntityTracker.isRunning}
                hidden={modalsState.isDeleteEntityModalHidden}
                onDelete={deleteEntity}
                onDismiss={() => setModalsState({ isDeleteEntityModalHidden: true })}
            />

            <EntityTable
                sortKey={sortKey}
                items={pageItems}
                isLoading={isLoading}
                selection={selection}
                entities={entityStores}
                areItemsFiltered={Boolean(query)}
                overallItemsCount={filteredEntities.length}
                onDeleteKeyUp={() => canDeleteData && setModalsState({ isDeleteEntityModalHidden: false })}
                onEntityClick={entityId => history.push(routeTable.luis.entityPage(appId, versionId, entityId))}
                onSortColumn={c => {
                    setSortKey({ key: c.key as keyof EntityStore, order: c.isSortedDescending ? 'asc' : 'desc' });
                    selection.setAllSelected(false);
                }}
            />

            {announcedComponent}

            <TablePagination pageCount={pageCount} currentPage={currentPage} onPageChange={p => setCurrentPage(p.selected)} />
        </Page>
    );
});
